import React, { useState } from 'react';
import { faCheck, faMinus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { featureRows, IMembershipPackage, membershipPackages } from '../assets/membership-options';

interface Props {
    onSelect?: (membership: IMembershipPackage) => void;
}

const MembershipTable: React.FC<Props> = ({ onSelect }) => {
    const [hoveredId, setHoveredId] = useState<number | null>(null);

    const colProps = (m: { id: number }) => ({
        onMouseEnter: () => setHoveredId(m.id),
        onMouseLeave: () => setHoveredId(null),
    });

    return (
        <>
            {/* Mobile: card layout */}
            <div className="md:hidden flex flex-col divide-y divide-gray-100">
                {membershipPackages.map(m => (
                    <div
                        key={m.id}
                        className={`p-6 ${m.isAdvised ? 'bg-primary-900' : 'bg-white'}`}
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <h4 className={`font-semibold tracking-widest text-sm uppercase ${m.isAdvised ? 'text-white' : 'text-black'}`}>
                                    {m.title}
                                </h4>
                                {m.isAdvised && (
                                    <span className="text-xs text-primary-300 tracking-wide">Populair</span>
                                )}
                            </div>
                            <div className="text-right">
                                <span className={`text-3xl font-light ${m.isAdvised ? 'text-white' : 'text-black'}`}>
                                    €{m.price}
                                </span>
                                <span className={`text-xs block ${m.isAdvised ? 'text-primary-300' : 'text-gray-400'}`}>
                                    / maand
                                </span>
                            </div>
                        </div>

                        <ul className="space-y-2 mb-5">
                            {featureRows.map(({ key, label }) => {
                                const val = m.features[key];
                                return (
                                    <li key={key} className="flex items-center justify-between">
                                        <span className={`text-sm ${m.isAdvised ? 'text-primary-200' : 'text-gray-600'}`}>
                                            {label}
                                        </span>
                                        {typeof val === 'boolean' ? (
                                            val
                                                ? <FontAwesomeIcon icon={faCheck} className="text-primary-500" />
                                                : <FontAwesomeIcon icon={faMinus} className="text-gray-300" />
                                        ) : (
                                            <span className={`text-sm font-medium ${m.isAdvised ? 'text-white' : 'text-gray-700'}`}>
                                                {val}
                                            </span>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>

                        {onSelect && (
                            <button
                                onClick={() => onSelect(m)}
                                className={`w-full ${m.isAdvised ? 'primary-button' : 'secondary-button'}`}
                            >
                                Kies {m.title}
                            </button>
                        )}
                    </div>
                ))}
            </div>

            {/* Desktop: table layout */}
            <div className="hidden md:block overflow-x-auto w-full">
                <table className="w-full border-collapse text-left">
                    <thead>
                        <tr>
                            <th className="p-4 md:p-5 w-1/3" />
                            {membershipPackages.map(m => (
                                <th
                                    key={m.id}
                                    {...colProps(m)}
                                    className={`p-4 md:p-5 text-center font-semibold tracking-widest text-xs uppercase border-b transition-colors cursor-default
                                        ${m.isAdvised
                                            ? hoveredId === m.id ? 'bg-primary-800 text-white border-primary-700' : 'bg-primary-900 text-white border-primary-800'
                                            : hoveredId === m.id ? 'bg-gray-100 text-black border-gray-200' : 'bg-white text-black border-gray-200'
                                        }`}
                                >
                                    {m.title}
                                    {m.isAdvised && (
                                        <div className="text-xs font-normal normal-case tracking-wide text-primary-300 pt-1">
                                            Populair
                                        </div>
                                    )}
                                </th>
                            ))}
                        </tr>
                        <tr>
                            <td className="p-4 md:p-5 text-xs font-semibold tracking-widest uppercase text-gray-500 border-b border-gray-200">
                                Prijs
                            </td>
                            {membershipPackages.map(m => (
                                <td
                                    key={m.id}
                                    {...colProps(m)}
                                    className={`p-4 md:p-5 text-center border-b transition-colors
                                        ${m.isAdvised
                                            ? hoveredId === m.id ? 'bg-primary-800 border-primary-700' : 'bg-primary-900 border-primary-800'
                                            : hoveredId === m.id ? 'bg-gray-100 border-gray-200' : 'bg-white border-gray-200'
                                        }`}
                                >
                                    <span className={`text-2xl md:text-3xl font-light ${m.isAdvised ? 'text-white' : 'text-black'}`}>
                                        €{m.price}
                                    </span>
                                    <span className={`text-xs block mt-0.5 ${m.isAdvised ? 'text-primary-300' : 'text-gray-400'}`}>
                                        / maand
                                    </span>
                                </td>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {featureRows.map(({ key, label, tooltip }, i) => (
                            <tr key={key} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                                <td className="p-4 md:p-5 text-sm text-gray-600 tracking-wide border-b border-gray-100">
                                    {tooltip ? (
                                        <span className="relative group inline-flex items-center gap-1.5 cursor-default">
                                            {label}
                                            <span className="text-xs text-primary-400 font-bold leading-none">ⓘ</span>
                                            <span className="absolute left-0 top-full mt-2 w-64 bg-gray-900 text-white text-xs rounded p-3 shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-20 leading-relaxed">
                                                {tooltip}
                                            </span>
                                        </span>
                                    ) : label}
                                </td>
                                {membershipPackages.map(m => {
                                    const val = m.features[key];
                                    return (
                                        <td
                                            key={m.id}
                                            {...colProps(m)}
                                            className={`p-4 md:p-5 text-center border-b border-gray-100 transition-colors
                                                ${m.isAdvised
                                                    ? hoveredId === m.id ? 'bg-primary-100' : 'bg-primary-50'
                                                    : hoveredId === m.id ? 'bg-gray-100' : ''
                                                }`}
                                        >
                                            {typeof val === 'boolean' ? (
                                                val
                                                    ? <FontAwesomeIcon icon={faCheck} className="text-primary-500" />
                                                    : <FontAwesomeIcon icon={faMinus} className="text-gray-300" />
                                            ) : (
                                                <span className="text-sm text-gray-600 whitespace-nowrap">{val}</span>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        {onSelect && (
                            <tr className="bg-white">
                                <td className="p-4 md:p-5" />
                                {membershipPackages.map(m => (
                                    <td
                                        key={m.id}
                                        {...colProps(m)}
                                        className={`p-4 md:p-5 text-center transition-colors
                                            ${m.isAdvised
                                                ? hoveredId === m.id ? 'bg-primary-100' : 'bg-primary-50'
                                                : hoveredId === m.id ? 'bg-gray-100' : ''
                                            }`}
                                    >
                                        <button
                                            onClick={() => onSelect(m)}
                                            className={m.isAdvised ? 'primary-button' : 'secondary-button'}
                                        >
                                            Kies {m.title}
                                        </button>
                                    </td>
                                ))}
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </>
    );
};

export default MembershipTable;
